// umlaut-check: skip — technische Bezeichner (Pruefung, pruefung.*) bleiben ASCII
import React, {useState} from "react";
import {Button, Card, Form, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import {Link} from "@inertiajs/react";
import axios from "axios";
import Icon from "@mdi/react";
import {mdiArrowLeft, mdiEmailFastOutline} from "@mdi/js";
import HelpTooltip from "../../Components/HelpTooltip.jsx";
import SelectList from "../../Components/SelectList.jsx";
import {TOOLTIP_DELAY, fixedPopper} from "../../Components/tooltipConfig.js";

/**
 * Einstellungen zu „Meine offenen Vorgänge“ (Plan #82, Stufe 1), nur für den
 * Superadmin. Speichern im Hintergrund, ohne Neuladen der Seite. Alle Werte
 * wirken sofort, ohne Deploy (Klärungspunkte #278 bis #280 sind offen).
 */

const GRUPPEN = [
    ["container", "Container oder Palette", "Erkannt am Versanddienstleister des Auftrags (Stichworte unten); hat Vorrang vor dem Vertriebskanal."],
    ["endkunde", "Endkunden", "Vertriebskanal GROSS1."],
    ["handel", "Handel, Key-Account, Industrie", "Vertriebskanäle NET1 bis NET4 und alles, was keiner anderen Gruppe angehört."],
    ["zoll", "Zoll-Proforma", "Vertriebskanal NET5."],
];
const WOCHENTAGE = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

const zeitpunkt = (iso) => iso ? new Date(iso).toLocaleString("de-DE", {timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"}) + " Uhr" : "–";

function Tipp({text, children}) {
    return (
        <OverlayTrigger placement="top" delay={TOOLTIP_DELAY} popperConfig={fixedPopper()} overlay={<Tooltip>{text}</Tooltip>}>
            {children}
        </OverlayTrigger>
    );
}

function Schalter({wert, optionen, onChange, label}) {
    return (
        <div className="pv-schalter" role="radiogroup" aria-label={label}>
            {optionen.map(([id, text]) => (
                <button key={String(id)} type="button" role="radio" aria-checked={wert === id} onClick={() => onChange(id)}>{text}</button>
            ))}
        </div>
    );
}

function Feld({label, hilfe, hinweis, fehler, children}) {
    return (
        <div className="pv-feld">
            <label>{label}{hilfe && <HelpTooltip placement="right" size={0.7}>{hilfe}</HelpTooltip>}</label>
            {children}
            {hinweis && <div className="pv-hinweis">{hinweis}</div>}
            {fehler && <div className="pv-feldfehler" role="alert">{fehler}</div>}
        </div>
    );
}

export default function Einstellungen({werte: start, vorgaben, personen, letzterAbgleich, letzteMail}) {
    const [werte, setWerte] = useState({
        ...start,
        ruhezeit_tage: String(start.ruhezeit_tage),
        fristen: Object.fromEntries(Object.entries(start.fristen).map(([g, f]) => [g, {gelb: String(f.gelb), rot: String(f.rot)}])),
    });
    const [fehler, setFehler] = useState({});
    const [speichert, setSpeichert] = useState(false);
    const [meldung, setMeldung] = useState(null);
    const [testPerson, setTestPerson] = useState(personen[0]?.id ?? "");
    const [testLaeuft, setTestLaeuft] = useState(false);

    const setze = (feld, wert) => setWerte((w) => ({...w, [feld]: wert}));
    const setzeFrist = (gruppe, stufe, wert) => setWerte((w) => ({...w, fristen: {...w.fristen, [gruppe]: {...w.fristen[gruppe], [stufe]: wert.replace(/\D/g, "")}}}));

    const speichern = async () => {
        setSpeichert(true);
        setFehler({});
        setMeldung(null);
        try {
            await axios.put("/dashboard/vorgaenge/einstellungen", {
                ...werte,
                ruhezeit_tage: Number(werte.ruhezeit_tage),
                mail_wochentag: Number(werte.mail_wochentag),
                fristen: Object.fromEntries(Object.entries(werte.fristen).map(([g, f]) => [g, {gelb: Number(f.gelb), rot: Number(f.rot)}])),
            });
            setMeldung({art: "ok", text: "Gespeichert. Die Werte gelten ab sofort."});
        } catch (e) {
            const errors = e.response?.data?.errors || {};
            setFehler(Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])));
            setMeldung({art: "fehler", text: Object.keys(errors).length ? "Bitte die markierten Felder prüfen." : "Speichern fehlgeschlagen: " + e.message});
        } finally {
            setSpeichert(false);
        }
    };

    const testmail = async () => {
        setTestLaeuft(true);
        setMeldung(null);
        try {
            const {data} = await axios.post("/dashboard/vorgaenge/testmail", {user_id: testPerson});
            setMeldung({art: "ok", text: data.meldung});
        } catch (e) {
            setMeldung({art: "fehler", text: e.response?.data?.meldung || "Testmail fehlgeschlagen: " + e.message});
        } finally {
            setTestLaeuft(false);
        }
    };

    return (
        <div className="pv-root">
            <div className="pv-kopfzeile">
                <div>
                    <h1 className="pv-titel">
                        Einstellungen: Meine offenen Vorgänge
                        <HelpTooltip placement="bottom">Nur für dich als Superadmin. Alle Werte wirken sofort beim nächsten Seitenaufruf und beim nächsten Abgleich, ohne Deploy.</HelpTooltip>
                    </h1>
                </div>
                <Tipp text="Zurück zum Dashboard „Meine offenen Vorgänge“.">
                    <Link href="/dashboard" className="btn btn-sm btn-outline-secondary"><Icon path={mdiArrowLeft} size={0.65} className="me-1"/>Zum Dashboard</Link>
                </Tipp>
            </div>

            {meldung && (
                <div className="pv-meldung" role="status" style={meldung.art === "fehler" ? {borderLeftColor: "var(--pv-rot)"} : undefined}>
                    {meldung.text}
                    <button type="button" className="pv-meldung__zu" aria-label="Meldung schließen" onClick={() => setMeldung(null)}>×</button>
                </div>
            )}

            <div className="pv-form">
                <Card className="pv-baustein">
                    <Card.Header><h2 className="pv-baustein__titel">Sichtbarkeit und Versand</h2></Card.Header>
                    <Card.Body>
                        <Feld label="Sichtbarkeit" hilfe="„Nur Superadmin“: Nur du siehst das neue Dashboard, für alle anderen bleibt alles wie bisher. „Alle mit Weclapp-ID“: Jede Person mit Weclapp-ID am Benutzer sieht auf /dashboard ihre eigenen Vorgänge, mit Zähler am Menüpunkt."
                              hinweis={werte.sichtbarkeit === "alle" ? "Achtung: Das Freischalten für alle ist laut Plan #82 erst nach Jürgens Zustimmung vorgesehen (Phase 5)." : "Auslieferungsstand: nur du."}>
                            <div><Schalter label="Sichtbarkeit" wert={werte.sichtbarkeit} onChange={(v) => setze("sichtbarkeit", v)} optionen={[["superadmin", "Nur Superadmin"], ["alle", "Alle mit Weclapp-ID"]]}/></div>
                        </Feld>
                        <Feld label="Wochenmail verschicken" hilfe="Schaltet den Versand der Wochenmail an die Mitarbeiter ein oder aus. Sie geht nur bei Sichtbarkeit „Alle mit Weclapp-ID“ und nur auf dem Live-System raus."
                              hinweis={werte.versand ? "Achtung: Der Versand an Mitarbeiter ist laut Plan #82 erst nach Jürgens Zustimmung vorgesehen." : "Versand ist aus – Testmails gehen trotzdem."}>
                            <div><Schalter label="Wochenmail verschicken" wert={werte.versand} onChange={(v) => setze("versand", v)} optionen={[[false, "Aus"], [true, "An"]]}/></div>
                        </Feld>
                        <Feld label="Testempfänger" fehler={fehler.testempfaenger} hilfe="Ist hier eine Adresse eingetragen, gehen ALLE Wochenmails an sie statt an die Personen (Betreff mit „[Test für …]“). Leer lassen erst für den echten Versand.">
                            <Form.Control type="text" inputMode="email" value={werte.testempfaenger} onChange={(e) => setze("testempfaenger", e.target.value)} aria-label="Testempfänger"/>
                        </Feld>
                        <div className="d-flex flex-wrap gap-3">
                            <Feld label="Wochentag der Mail" hilfe="An diesem Tag geht die Wochenmail ab der eingestellten Uhrzeit raus, höchstens einmal je Person und Woche.">
                                <div style={{minWidth: 180}}>
                                    <SelectList ariaLabel="Wochentag der Mail" value={Number(werte.mail_wochentag)} options={WOCHENTAGE.map((t, i) => ({value: i + 1, label: t}))} onChange={(v) => setze("mail_wochentag", v)}/>
                                </div>
                            </Feld>
                            <Feld label="Uhrzeit" fehler={fehler.mail_uhrzeit} hilfe="Ab dieser Uhrzeit (Berliner Zeit) geht die Mail am eingestellten Wochentag raus. Format HH:MM.">
                                <Form.Control type="text" inputMode="numeric" style={{maxWidth: 100}} value={werte.mail_uhrzeit} onChange={(e) => setze("mail_uhrzeit", e.target.value)} aria-label="Uhrzeit"/>
                            </Feld>
                        </div>
                        <Feld label="Testmail" hilfe="Schickt die Wochenmail, wie diese Person sie bekäme, sofort an den Testempfänger – auch bei ausgeschaltetem Versand. Gespeichert wird dabei nichts.">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <div style={{minWidth: 240}}>
                                    <SelectList ariaLabel="Testmail für" value={testPerson} options={personen.map((p) => ({value: p.id, label: `Mail von ${p.name}`}))} onChange={setTestPerson}/>
                                </div>
                                <Tipp text="Verschickt jetzt eine Testmail an den gespeicherten Testempfänger.">
                                    <Button size="sm" variant="outline-secondary" onClick={testmail} disabled={testLaeuft || !testPerson}>
                                        {testLaeuft ? <Spinner animation="border" size="sm" className="me-1"/> : <Icon path={mdiEmailFastOutline} size={0.65} className="me-1"/>}
                                        Testmail senden
                                    </Button>
                                </Tipp>
                            </div>
                        </Feld>
                    </Card.Body>
                </Card>

                <Card className="pv-baustein" id="vorfuehrung">
                    <Card.Header><h2 className="pv-baustein__titel">Vorführansicht <HelpTooltip>Was ein Konto mit dem geschützten Recht „Dashboard: Vorführansicht“ (z. B. der Meetingraum) auf /dashboard sieht: echte Vorgänge ohne Personennamen und ohne Hinweis auf einen Demomodus. Klicks werden nie gespeichert, es geht keine Mail raus, nach dem Neuladen beginnt die Ansicht von vorn.</HelpTooltip></h2></Card.Header>
                    <Card.Body>
                        <Feld label="Inhalt der Vorführansicht" fehler={fehler.vorfuehrung} hilfe="„Gemischt“ zeigt rund 15 Aufträge über mehrere Personen (gelb und rot, mindestens einer mit „Rechnung bezahlt“) und alle Rechnungen ohne offenen Posten. Eine Person zeigt genau deren Vorgänge, ohne ihren Namen.">
                            <div style={{maxWidth: 320}}>
                                <SelectList ariaLabel="Inhalt der Vorführansicht" value={String(werte.vorfuehrung)} options={[{value: "gemischt", label: "Gemischt (mehrere Personen)"}, ...personen.map((p) => ({value: String(p.id), label: `Vorgänge von ${p.name}`}))]} onChange={(v) => setze("vorfuehrung", String(v))}/>
                            </div>
                        </Feld>
                    </Card.Body>
                </Card>

                <Card className="pv-baustein">
                    <Card.Header><h2 className="pv-baustein__titel">Offene Aufträge <HelpTooltip>Ein Auftrag wird zum Vorgang, sobald er so viele Tage ohne Bearbeitung ist wie „Gelb ab“; ab „Rot ab“ wird die Ampel rot. Bearbeitung heißt: Änderung am Auftrag oder an einem seiner Lieferscheine. Klärungspunkt #278.</HelpTooltip></h2></Card.Header>
                    <Card.Body>
                        <table className="pv-fristen">
                            <thead><tr><th>Gruppe</th><th>Gelb ab (Tage)</th><th>Rot ab (Tage)</th></tr></thead>
                            <tbody>
                            {GRUPPEN.map(([g, titel, tipp]) => (
                                <tr key={g}>
                                    <td><span className="pv-kopf">{titel}<HelpTooltip size={0.7}>{tipp} Vorgabe: gelb {vorgaben.fristen[g].gelb}, rot {vorgaben.fristen[g].rot} Tage.</HelpTooltip></span></td>
                                    <td><Form.Control size="sm" type="text" inputMode="numeric" value={werte.fristen[g].gelb} onChange={(e) => setzeFrist(g, "gelb", e.target.value)} aria-label={`${titel}: gelb ab`}/></td>
                                    <td>
                                        <Form.Control size="sm" type="text" inputMode="numeric" value={werte.fristen[g].rot} onChange={(e) => setzeFrist(g, "rot", e.target.value)} aria-label={`${titel}: rot ab`}/>
                                        {fehler[`fristen.${g}.rot`] && <div className="pv-feldfehler">{fehler[`fristen.${g}.rot`]}</div>}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        <Feld label="Stichworte für Container oder Palette" hilfe="Enthält der Versanddienstleister des Auftrags eines dieser Wörter (Groß-/Kleinschreibung egal), gilt die Container-Frist. Mehrere mit Komma trennen." hinweis={`Vorgabe: ${vorgaben.container_stichworte}`}>
                            <Form.Control type="text" value={werte.container_stichworte} onChange={(e) => setze("container_stichworte", e.target.value)} aria-label="Stichworte für Container oder Palette"/>
                        </Feld>
                        <Feld label="Ruhezeit nach „Ja“ (Tage)" fehler={fehler.ruhezeit_tage} hilfe="So lange verschwindet ein Vorgang nach „Ja, berechtigt offen“. Danach kommt er wieder, falls er noch offen ist.">
                            <Form.Control type="text" inputMode="numeric" style={{maxWidth: 100}} value={werte.ruhezeit_tage} onChange={(e) => setze("ruhezeit_tage", e.target.value.replace(/\D/g, ""))} aria-label="Ruhezeit in Tagen"/>
                        </Feld>
                    </Card.Body>
                </Card>

                <Card className="pv-baustein">
                    <Card.Header><h2 className="pv-baustein__titel">Rechnungen ohne offenen Posten <HelpTooltip>Rechnungen, die mehr als einen Werktag nach dem Rechnungsdatum noch keinen offenen Posten haben und nicht storniert sind. Klärungspunkt #280.</HelpTooltip></h2></Card.Header>
                    <Card.Body>
                        <Feld label="Zuständig für eine Rechnung" hilfe="Bei wem die Rechnung erscheint: bei der in Weclapp verantwortlichen Person oder bei der, die sie erstellt hat.">
                            <div><Schalter label="Zuständig für eine Rechnung" wert={werte.rechnung_zustaendig} onChange={(v) => setze("rechnung_zustaendig", v)} optionen={[["verantwortlich", "Verantwortliche(r)"], ["ersteller", "Ersteller(in)"]]}/></div>
                        </Feld>
                        <Feld label="Entwürfe mitzählen" hilfe="Rechnungsentwürfe (Status „Neu“, vorläufige Nummer) zählen nur, wenn das eingeschaltet ist. Vorgabe: aus.">
                            <div><Schalter label="Entwürfe mitzählen" wert={werte.entwuerfe_mitzaehlen} onChange={(v) => setze("entwuerfe_mitzaehlen", v)} optionen={[[false, "Nein"], [true, "Ja"]]}/></div>
                        </Feld>
                        <Feld label="Ausgenommene Rechnungsarten" hilfe="Weclapp-Rechnungsarten, die nie als Vorgang erscheinen, z. B. CREDIT_NOTE. Mehrere mit Komma trennen. Am 30.10.2026 bekamen alle vorkommenden Arten einen offenen Posten, deshalb ist die Liste leer."
                              hinweis="Mögliche Werte: STANDARD_INVOICE, CREDIT_NOTE, ADVANCE_PAYMENT_INVOICE, PREPAYMENT_INVOICE, FINAL_INVOICE">
                            <Form.Control type="text" value={werte.ausgenommene_rechnungsarten} onChange={(e) => setze("ausgenommene_rechnungsarten", e.target.value)} aria-label="Ausgenommene Rechnungsarten"/>
                        </Feld>
                    </Card.Body>
                </Card>

                <div className="d-flex align-items-center gap-3 mb-4">
                    <Tipp text="Speichert alle Werte dieser Seite. Sie gelten sofort.">
                        <Button variant="dark" onClick={speichern} disabled={speichert}>
                            {speichert && <Spinner animation="border" size="sm" className="me-2"/>}Speichern
                        </Button>
                    </Tipp>
                </div>

                <Card className="pv-baustein">
                    <Card.Header><h2 className="pv-baustein__titel">Protokoll <HelpTooltip>Letzter Abgleich und letzte Mail – nur Zeitpunkte und Mengen, ohne Angaben je Person.</HelpTooltip></h2></Card.Header>
                    <Card.Body className="pv-lauf">
                        <div>Letzter Abgleich: {letzterAbgleich ? `${zeitpunkt(letzterAbgleich.beendet_at)} – ${letzterAbgleich.erfolgreich ? `${letzterAbgleich.auftraege} offene Aufträge, ${letzterAbgleich.rechnungen} Rechnungen ohne offenen Posten geholt` : `fehlgeschlagen: ${letzterAbgleich.meldung}`}` : "noch keiner"}</div>
                        <div>Letzte Mail: {letzteMail ? `${zeitpunkt(letzteMail.beendet_at)} – ${letzteMail.empfaenger ?? 0} Empfänger, Absender ${letzteMail.absender || "–"}${letzteMail.meldung ? ` (${letzteMail.meldung})` : ""}` : "noch keine"}</div>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
}
